/**
 * Edge-connected flood-fill background removal for AI material tiles
 * (H-2026-09-13-139). Pure JS, zero dependencies — safe for both Workers
 * (no canvas/sharp) and the creator-hub browser bundle.
 *
 * Contract:
 * - Deterministic: same pixels in, same pixels out (content_hash stability).
 * - In-place: `floodFillTransparent` mutates the passed RGBA buffer.
 * - Conservative: only near-white regions CONNECTED to the image border are
 *   cleared, so enclosed whites (cup highlights, whites inside the subject)
 *   survive. `isWhiteCornerBackground` gates the whole operation so
 *   patterned backgrounds pass through untouched.
 *
 * Thresholds are empirical: klein/JPEG whites measure 253-255 with halos in
 * the 240-254 band; FLUX 3 PNG "white" backgrounds render as a uniform
 * 240-244 gray-white (measured 2026-10-24), so the near-white floor sits
 * below that band at 238 and the feather band widens to (232, 255).
 */

/** Per-channel floor for "near white" flood-fill seeds/expansion. */
const NEAR_WHITE_MIN = 238
/** Corner-patch mean luminance floor for the white-background verdict. */
const CORNER_MEAN_MIN = 240
/** Corner sample patch edge (px), clamped to the image size. */
const CORNER_PATCH_PX = 16
/** Feather band: kept boundary pixels with luminance in (232, 255) get a
 *  partially transparent alpha, interpolating 255 -> 0. */
const FEATHER_LO = 232
const FEATHER_HI = 255

/**
 * Mean luminance of the px*px patch whose top-left corner is (x0, y0).
 * @param {Uint8Array | Uint8ClampedArray} rgba
 * @param {number} width
 * @param {number} x0
 * @param {number} y0
 * @param {number} px
 * @returns {number} mean of (r+g+b)/3 over the patch
 */
function patchMeanLuminance(rgba, width, x0, y0, px) {
  let sum = 0
  for (let y = y0; y < y0 + px; y++) {
    for (let x = x0; x < x0 + px; x++) {
      const o = (y * width + x) * 4
      sum += (rgba[o] + rgba[o + 1] + rgba[o + 2]) / 3
    }
  }
  return sum / (px * px)
}

/**
 * White-background verdict: all four corner 16x16 patches must average
 * >= 240 luminance. Any dark/colorful corner means a patterned background —
 * the caller must then keep the tile as-is.
 * @param {Uint8Array | Uint8ClampedArray} rgba - RGBA pixel buffer
 * @param {number} width - image width (px)
 * @param {number} height - image height (px)
 * @returns {boolean} true when every corner reads as white background
 */
export function isWhiteCornerBackground(rgba, width, height) {
  const px = Math.min(CORNER_PATCH_PX, width, height)
  if (px < 1) return false
  const origins = [
    [0, 0],
    [width - px, 0],
    [0, height - px],
    [width - px, height - px],
  ]
  for (const [x0, y0] of origins) {
    if (patchMeanLuminance(rgba, width, x0, y0, px) < CORNER_MEAN_MIN) return false
  }
  return true
}

/**
 * Clear the border-connected near-white background to alpha=0, in place.
 *
 * Pass 1 — flood fill: a stack-based 4-connected fill seeded from every
 * near-white border pixel expands through near-white pixels only; reached
 * pixels get alpha=0. Interior white islands (not touching the border
 * through near-white pixels) keep alpha=255.
 *
 * Pass 2 — feather: kept pixels 4-adjacent to a cleared pixel get their
 * alpha interpolated by luminance across the (240, 255) band, softening
 * the JPEG white halo at the subject edge. Dark edge pixels stay opaque.
 *
 * @param {Uint8Array | Uint8ClampedArray} rgba - RGBA pixel buffer (mutated)
 * @param {number} width - image width (px)
 * @param {number} height - image height (px)
 * @returns {void}
 */
export function floodFillTransparent(rgba, width, height) {
  const count = width * height
  const removed = new Uint8Array(count)
  const stack = []

  // @param {number} i pixel index
  const isNearWhite = (i) => {
    const o = i * 4
    return (
      rgba[o] >= NEAR_WHITE_MIN &&
      rgba[o + 1] >= NEAR_WHITE_MIN &&
      rgba[o + 2] >= NEAR_WHITE_MIN
    )
  }

  // @param {number} i pixel index
  const seed = (i) => {
    if (!removed[i] && isNearWhite(i)) {
      removed[i] = 1
      stack.push(i)
    }
  }

  // Seed from the four borders.
  for (let x = 0; x < width; x++) {
    seed(x) // top row
    seed((height - 1) * width + x) // bottom row
  }
  for (let y = 0; y < height; y++) {
    seed(y * width) // left column
    seed(y * width + width - 1) // right column
  }

  // Pass 1 — expand the fill through near-white pixels only.
  while (stack.length > 0) {
    const i = stack.pop()
    const x = i % width
    const y = (i / width) | 0
    if (x > 0) seed(i - 1)
    if (x < width - 1) seed(i + 1)
    if (y > 0) seed(i - width)
    if (y < height - 1) seed(i + width)
  }

  for (let i = 0; i < count; i++) {
    if (removed[i]) rgba[i * 4 + 3] = 0
  }

  // Pass 2 — 1px feather ring on kept pixels bordering the cleared region.
  for (let i = 0; i < count; i++) {
    if (removed[i]) continue
    const x = i % width
    const y = (i / width) | 0
    const touchesCleared =
      (x > 0 && removed[i - 1]) ||
      (x < width - 1 && removed[i + 1]) ||
      (y > 0 && removed[i - width]) ||
      (y < height - 1 && removed[i + width])
    if (!touchesCleared) continue
    const o = i * 4
    const lum = (rgba[o] + rgba[o + 1] + rgba[o + 2]) / 3
    if (lum >= FEATHER_HI) {
      rgba[o + 3] = 0
    } else if (lum > FEATHER_LO) {
      const alpha = Math.round((255 * (FEATHER_HI - lum)) / (FEATHER_HI - FEATHER_LO))
      if (alpha < rgba[o + 3]) rgba[o + 3] = alpha
    }
  }
}

/**
 * Defringe pass (2026-10-29, bao: white halo read as a defect on dark
 * garments). Runs AFTER floodFillTransparent. Two steps:
 *
 * 1. Erode the alpha mask by ERODE_PX: kept pixels 8-adjacent to a cleared
 *    pixel are cleared too. The flood fill's boundary stops at the first
 *    non-near-white pixel, but the model's anti-aliasing band is wider than
 *    the 238 floor — that ring is exactly the visible halo, so it goes.
 * 2. Decontaminate the new opaque edge band: edge pixels' RGB still carries
 *    white mixed in from the removed background (alpha only fixes coverage,
 *    not color). Each edge pixel's RGB is replaced by the mean of its opaque
 *    non-edge 8-neighbours — the subject's own colour at that spot — so no
 *    white survives to glow on black fabric.
 *
 * In-place, deterministic, zero-dependency like the flood fill.
 * @param {Uint8Array | Uint8ClampedArray} rgba
 * @param {number} width
 * @param {number} height
 */
export function defringeWhiteMatte(rgba, width, height) {
  const count = width * height
  const cleared = new Uint8Array(count)
  for (let i = 0; i < count; i++) if (rgba[i * 4 + 3] === 0) cleared[i] = 1
  if (!cleared.some(Boolean)) return // nothing was removed — no fringe to fix

  // Step 1 — 1px erosion (8-connected).
  const erode = []
  for (let i = 0; i < count; i++) {
    if (cleared[i]) continue
    const x = i % width
    const y = (i / width) | 0
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx
        const ny = y + dy
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue
        if (cleared[ny * width + nx]) {
          erode.push(i)
          dy = 2 // break outer loop too
          break
        }
      }
    }
  }
  for (const i of erode) rgba[i * 4 + 3] = 0

  // Step 2 — colour decontamination of the (new) opaque edge band.
  const isCleared = (i) => rgba[i * 4 + 3] === 0
  const edge = []
  for (let i = 0; i < count; i++) {
    if (isCleared(i)) continue
    const x = i % width
    const y = (i / width) | 0
    let touches = false
    for (let dy = -1; dy <= 1 && !touches; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx
        const ny = y + dy
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue
        if (isCleared(ny * width + nx)) {
          touches = true
          break
        }
      }
    }
    if (touches) edge.push(i)
  }
  const isEdge = new Set(edge)
  for (const i of edge) {
    const x = i % width
    const y = (i / width) | 0
    let r = 0
    let g = 0
    let b = 0
    let n = 0
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx
        const ny = y + dy
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue
        const j = ny * width + nx
        // Donors: opaque, not on the edge themselves (deeper subject pixels).
        if (isCleared(j) || isEdge.has(j)) continue
        const o = j * 4
        r += rgba[o]
        g += rgba[o + 1]
        b += rgba[o + 2]
        n++
      }
    }
    if (!n) continue // isolated speck — leave it
    const o = i * 4
    rgba[o] = Math.round(r / n)
    rgba[o + 1] = Math.round(g / n)
    rgba[o + 2] = Math.round(b / n)
  }
}
